import React from "react";

import { Icon } from "../components/ui";
import { formatBytes } from "./chatFormat";
import { extensionLabel, iconForKind, kindOf } from "./attachments";

/**
 * Everything attached to one message.
 *
 * Images are pulled out and laid out as a grid, because a merchant sending
 * four photos of a watch wants them read as one set, not as four stacked
 * cards. Everything else stacks as a card, with PDFs getting their own
 * treatment — they are the most common non-image attachment here (invoices,
 * warranty cards, service reports) and used to render as an anonymous grey
 * chip indistinguishable from a .zip.
 *
 * Every URL is the signed attachment route (BE #2405). It carries no auth
 * header, which is exactly why <img>, <video> and <a download> can use it
 * directly — a bearer-gated URL rendered every attachment broken.
 */

const MAX_TILES = 4;

const ImageGrid = ({ images, onOpen }) => {
  const shown = images.slice(0, MAX_TILES);
  const overflow = images.length - shown.length;

  return (
    <div className={`rc-chat__media rc-chat__media--${Math.min(shown.length, MAX_TILES)}`}>
      {shown.map((attachment, position) => (
        <button
          key={attachment.index}
          type="button"
          className="rc-chat__tile"
          onClick={() => onOpen?.(images, images.indexOf(attachment))}
          aria-label={`Open ${attachment.name || "image"}`}
        >
          <img
            src={attachment.thumbnail_url || attachment.url}
            alt={attachment.name || ""}
            loading="lazy"
          />
          {/* The counter sits on the last visible tile rather than adding a
              fifth one, so the grid keeps its shape at any count. */}
          {overflow > 0 && position === shown.length - 1 && (
            <span className="rc-chat__tile-more">+{overflow}</span>
          )}
        </button>
      ))}
    </div>
  );
};

const FileCard = ({ attachment }) => {
  const kind = kindOf(attachment);

  return (
    <a
      className={`rc-chat__doc rc-chat__doc--${kind}`}
      href={attachment.url}
      target="_blank"
      rel="noopener noreferrer"
      // `download` is a hint, not a guarantee: a PDF opens in the browser's
      // viewer because that is what people expect from an invoice, while a
      // .zip downloads. Both are reachable either way.
      download={kind === "pdf" ? undefined : attachment.name || undefined}
      title={attachment.name}
    >
      <span className="rc-chat__doc-badge" aria-hidden="true">
        <Icon name={iconForKind(kind)} size="sm" />
        <span className="rc-chat__doc-ext">{extensionLabel(attachment)}</span>
      </span>

      <span className="rc-chat__doc-main">
        <span className="rc-chat__doc-name">{attachment.name || "Attachment"}</span>
        {/* Size alone, as one text node. "Opens in a new tab" used to be
            appended here, but the ExternalLink glyph already says that and
            the extra words made the size unfindable as a string on its
            own — worse for a screen reader, not better. */}
        <span className="rc-chat__doc-meta">
          {attachment.size ? formatBytes(attachment.size) : extensionLabel(attachment)}
        </span>
      </span>

      <span className="rc-chat__doc-action" aria-hidden="true">
        <Icon name={kind === "pdf" ? "ExternalLink" : "Download"} size="sm" />
      </span>
    </a>
  );
};

const MessageAttachments = ({ attachments = [], onOpenImage }) => {
  if (!attachments.length) return null;

  const images = attachments.filter((attachment) => kindOf(attachment) === "image");
  const others = attachments.filter((attachment) => kindOf(attachment) !== "image");

  return (
    <div className="rc-chat__attachments">
      {images.length > 0 && <ImageGrid images={images} onOpen={onOpenImage} />}

      {others.map((attachment) => {
        const kind = kindOf(attachment);

        if (kind === "video") {
          return (
            <video
              key={attachment.index}
              className="rc-chat__video"
              src={attachment.url}
              controls
              preload="metadata"
            />
          );
        }

        if (kind === "audio") {
          return (
            <div className="rc-chat__audio" key={attachment.index}>
              <audio src={attachment.url} controls preload="metadata" />
              {/* Voice notes relayed from Roam arrive as audio files with
                  machine names, so the filename is only worth showing when
                  it tells you something. */}
              {attachment.name && !/^voice|^audio/i.test(attachment.name) && (
                <span className="rc-chat__audio-name">{attachment.name}</span>
              )}
            </div>
          );
        }

        return <FileCard key={attachment.index} attachment={attachment} />;
      })}
    </div>
  );
};

export default MessageAttachments;
